//OnBoardingPage.tsx

"use client";
import {
  MobilityProcessRespone,
  StartProcessRequest,
  extractData,
  postMobilityProcessStartProcess,
} from "@/api";
import { Steps } from "@/components/Steps/Steps";
import { TextFieldCustom } from "@/components/TextFieldCustom";
import { formVar, proccesVar, showGeneralError, userVar } from "@/store";
import { FormState } from "@/store/indes.modal";
import colors from "@/theme/colors";
import sizes from "@/theme/sizes";

import { trackCustomEvent } from "@/common/gtm";
import { Desktop, Mobile } from "@/common/MediaQueries";
import { Pages } from "@/common/pages.modal";
import CheckBoxWithText from "@/components/CheckBoxWithText";
import { ErrorMoadlWrapper, Modal } from "@/components/Modal/Modal";
import theme from "@/theme/theme";
import styled from "@emotion/styled";
import { yupResolver } from "@hookform/resolvers/yup";
import { Button, CircularProgress, Dialog, Typography } from "@mui/material";
import Image from "next/image";
import Link from "next/link";
import { useRouter } from "next/navigation";
import { useEffect, useState } from "react";
import { FieldErrors, SubmitHandler, useForm, useWatch } from "react-hook-form";
import {
  ImageWrapper,
  StyledWrapper,
  TextWrapper,
  TextWrapper2,
} from "../shared.layout.style";
import { validationSchema } from "./validate";
import { AutocompleteFieldCustom } from "@/components/AutocompleteFieldCustom";

const StyledTextWrapper = styled(TextWrapper2)`
  align-items: flex-start;
`;

const StyledButton = styled(Button)<{ loading?: string }>((props) => ({
  width: "80%",
  height: sizes.button.rounded.lg.height,
  borderRadius: sizes.button.rounded.lg.borderRadius,
  display: "flex",
  justifyContent: "space-evenly",
  maxWidth: 400,
  margin: 20,
  opacity: props.loading ? 0.8 : 1,
}));

const StyledFormWrapper = styled.div`
  display: flex;
  gap: 65px 64px;
  justify-content: flex-start;
  align-items: flex-start;
  width: 720px;
  flex-wrap: wrap;
  margin-bottom: 64px;

  > * {
    width: 328px;
  }

  ${() => theme.breakpoints.down("sm")} {
    width: 90%;
    min-width: 50px;
    margin-bottom: 0;
    gap: 34px;
  }
`;

const StyleAddressWrapper = styled.div`
  width: 720px;
  margin-top: 5px;

  ${() => theme.breakpoints.down("sm")} {
    width: 90%;
    margin-top: 48px;
  }
`;

const StyledFormAddressWrapper = styled.div`
  display: flex;
  position: relative;
  gap: 20px;
  margin-bottom: 50px;

  > * {
    width: 176px;
  }

  > :first-child {
    width: 328px;
  }

  ${() => theme.breakpoints.down("sm")} {
    gap: 40px;
    flex-wrap: wrap;

    > * {
      flex: 1 0 40%;
    }

    > :first-child {
      flex: 1 0 100%;
    }
  }
`;

const StyledInfo = styled.div`
  width: 720px;

  ${() => theme.breakpoints.down("sm")} {
    width: 90%;
  }
`;

const StyledInfoText = styled.p`
  font-size: 1rem;
  font-style: normal;
  font-weight: 400;
  line-height: 22px;
  text-align: right;
`;

const CheckBoxWrapper = styled.div`
  display: flex;
  justify-content: flex-start;
  flex-direction: row;
  margin-top: 30px;
  min-height: 80px;
`;

const StyledCheckBoxWithText = styled(CheckBoxWithText)`
  position: relative;
  right: -3px;
  &:first-child {
    padding: 0 0 0 9px;
    ${() => theme.breakpoints.down("sm")} {
      align-items: flex-start;
      padding: 13px 13px 13px 9px;
    }
  }

  .MuiFormControlLabel-root {
    margin-right: 0;
    align-items: flex-start;
  }

  .MuiButtonBase-root.MuiCheckbox-root {
    padding-right: 0;
    padding-top: 0;
    position: relative;
    top: 3px;
  }

  .MuiFormHelperText-root.Mui-error {
    text-align: right;
  }
`;

const LOCAL_STORAGE_LEAD_ID_KEY = "lead_id";
type StartProcessPayload = StartProcessRequest & { lead_id?: string };

const getLeadIdFromLocalStorage = (): string | undefined => {
  if (typeof window === "undefined") return undefined;

  try {
    const directLeadId = window.localStorage
      .getItem(LOCAL_STORAGE_LEAD_ID_KEY)
      ?.trim();

    if (directLeadId) return directLeadId;

    const siteStorageValue = window.localStorage.getItem(window.location.origin);
    if (!siteStorageValue) return undefined;

    const parsedSiteStorageValue = JSON.parse(siteStorageValue);
    if (
      typeof parsedSiteStorageValue?.lead_id === "string" &&
      parsedSiteStorageValue.lead_id.trim()
    ) {
      return parsedSiteStorageValue.lead_id.trim();
    }
  } catch (error) {
    console.warn("Failed to read lead_id from localStorage", error);
  }

  return undefined;
};

export default function OnBoardingPage() {
  const router = useRouter();
  const hasReferralTitle = !!userVar().referralCode;
  const hasAffiliateTitle = !!userVar().affiliateId;
  const pageTitle = hasReferralTitle
    ? userVar().referralTitle
    : hasAffiliateTitle
      ? "חבר מביא חבר"
      : null;
  const pageSubtitle = hasReferralTitle ? userVar().referralSubTitle : null;

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(false);

  const [agreeToTerms, setAgreeToTerms] = useState(false);
  const [checkBoxError, setCheckBoxError] = useState(false);

  // --- cities / streets states for autocomplete
  const [cities, setCities] = useState<any[]>([]);
  const [selectedCity, setSelectedCity] = useState<any | null>(null);

  const {
    handleSubmit,
    control,
    watch,
    setValue,
    formState: { errors },
  } = useForm({
    mode: "onBlur",
    resolver: yupResolver(validationSchema),
    defaultValues: {
      firstName: formVar().firstName,
      lastName: formVar().lastName,
      phone: formVar().phone,
      id: formVar().id,
      email: formVar().email,
      agreeToTerms: false,
      city: formVar().city ?? "",
      street: formVar().street ?? "",
    },
    shouldFocusError: true,
  });

  const firstName = watch("firstName");
  const lastName = watch("lastName");
  const phone = watch("phone");
  const id = watch("id");
  const email = watch("email");

  // load cities.json
  useEffect(() => {
    fetch("cities.json")
      .then((res) => res.json())
      .then((data: any[]) => {
        setCities(data || []);
      })
      .catch((e) => {
        console.error("Failed to load cities.json", e);
        setCities([]);
      });
  }, []);

  // --- Watch city field and set selectedCity ---
  const cityName = useWatch({ control, name: "city" });
  useEffect(() => {
    if (cityName && cities.length) {
      const found = cities.find((c) => c["שם_ישוב"] === cityName);
      setSelectedCity(found ?? null);
    } else {
      setSelectedCity(null);
    }
  }, [cityName, cities]);

  // clear street when city changes
  useEffect(() => {
    setValue("street", "");
  }, [selectedCity]);

  useEffect(() => {
    formVar({ ...formVar(), firstName, lastName, phone, id, email });
  }, [firstName, lastName, phone, id, email, userVar().referralCode]);

  const onSubmit: SubmitHandler<Partial<FormState>> = async (data) => {
    trackCustomEvent("personal_details_step_1_submit");
    setLoading(true);
    try {
      const packageChosen = userVar().referralCode || ""; //|| packageParams;
      const startProcessPayload: StartProcessPayload = {
        personId: data.id!,
        city: data.city ?? "",
        street: data.street ?? "",
        houseNumber: data.houseNumber ?? "",
        apartmentNumber: data.apartmentNumber ?? "",
        entry: "",
        billingPlan: packageChosen,
        phoneNumber: data.phone!,
        firstName: data.firstName!,
        lastName: data.lastName!,
        email: data.email ?? "",
      };

      const leadId = getLeadIdFromLocalStorage();
      if (leadId) {
        startProcessPayload.lead_id = leadId;
      }

      if (userVar().refId) {
        startProcessPayload.refid = userVar().refId;
      }
      if (userVar().affiliateId) {
        startProcessPayload.affiliateId = userVar().affiliateId;
      }
      if (userVar().referralCampaignName) {
        startProcessPayload.campaignName = userVar().referralCampaignName;
      }
      console.log(startProcessPayload);
      console.log(
        "userVar().referralCampaignName",
        userVar().referralCampaignName,
      );

      const response: MobilityProcessRespone =
        await postMobilityProcessStartProcess(startProcessPayload).then(
          extractData,
        );
      if (!response.processId) throw new Error("processId is missing");
      if (response.processStatus === "DuplicateValue") {
        setError(true);
        return;
      }
      proccesVar({
        ...proccesVar(),
        processId: response.processId,
        processStatus: response.processStatus,
        phoneNumber: response.phoneNumber,
        smartMeter: response.smartMeter,
        billingPlan: response.billingPlan,
        refId: userVar().refId ?? "",
        referralCampaignName: userVar().referralCampaignName ?? "",
        affiliateId: userVar().affiliateId ?? "",
      });

      router.push(`/${Pages.OTP}`);
    } catch (error) {
      console.log("error", error);
      showGeneralError();
    } finally {
      setLoading(false);
    }
  };

  const onError = (errors: FieldErrors<Partial<FormState>>) => {
    console.log(errors);
  };

  const fields = [
    {
      id: "firstName",
      label: "שם פרטי",
      inputMode: "text",
      autocomplete: "given-name",
      type: "text",
      helperText:"", //"כפי שמופיע בחשבון החשמל",
    },
    {
      id: "lastName",
      label: "שם משפחה",
      inputMode: "text",
      autocomplete: "family-name",
      type: "text",
      helperText: "",//"כפי שמופיע בחשבון החשמל",
    },
    {
      id: "phone",
      label: "נייד",
      inputMode: "numeric",
      autocomplete: "tel-national",
      type: "tel",
      helperText: "מספר הנייד כפי שמעודכן בחברת החשמל",
    },
    {
      id: "id",
      label: "תעודת זהות",
      inputMode: "numeric",
      type: "text",
      helperText: "מספר בן 9 ספרות של בעל/ת החשבון",
    },
    {
      id: "email",
      label: "אימייל",
      inputMode: "email",
      autocomplete: "email",
      type: "email",
      helperText: "כדי שנוכל לעדכן בהתקדמות בתהליך",
    },
  ];

  useEffect(() => {
    userVar({ ...userVar(), progress: 1 });
    console.log("[gtm debug] personal_details_step1_screen", {
      selectedPlanNameString: userVar().selectedPlanNameString,
      referralTitle: userVar().referralTitle,
      package_type:userVar().selectedPlanNameString || userVar().referralTitle || "GENERAL", // || packageParams

    });
    trackCustomEvent(
      "personal_details_step1_screen",
      userVar().selectedPlanNameString || userVar().referralTitle || "GENERAL", // || packageParams
    );
    console.log(
      "personal_details_step1_screen",
      userVar().selectedPlanNameString || userVar().referralTitle || "GENERAL"
    );
  }, []);

  return (
    <StyledWrapper>
      <ImageWrapper>
        <Image
          src="./circle_bg.svg"
          alt="ספלאש לוגו"
          width={150}
          height={150}
          style={{ left: 0, top: -25, position: "absolute" }}
        />
      </ImageWrapper>
      <Steps current={0} />
      <StyledTextWrapper>
        <Desktop>
          {pageTitle ? (
            <Typography variant="h1" fontSize={40} color={colors.black}>
              {pageTitle}
            </Typography>
          ) : (
            <div>
              <Typography variant="h1" fontSize={40} color={colors.black}>
                איזה כיף שבחרת להצטרף ל-
              </Typography>
              <Typography variant="h1" fontSize={40} color={colors.black}>
                SUPER POWER
              </Typography>
            </div>
          )}

          {pageSubtitle ? (
            <Typography marginTop={2} variant="h3" fontSize={24} lineHeight={2}>
              {pageSubtitle}
            </Typography>
          ) : (
            <Typography marginTop={2} variant="h3" fontSize={24} lineHeight={2}>
              מצטרפים אונליין ומתחילים לחסוך
            </Typography>
          )}
        </Desktop>

        <Mobile>
          {pageTitle ? (
            <Typography
              variant="h1"
              textAlign="center"
              fontSize={20}
              color={colors.black}
            >
              {pageTitle}
            </Typography>
          ) : (
            <div>
              <Typography variant="h1" fontSize={20} color={colors.black}>
                איזה כיף שבחרת להצטרף ל-
              </Typography>
              <Typography variant="h1" fontSize={20} color={colors.black}>
                SUPER POWER
              </Typography>
            </div>
          )}

          {pageSubtitle ? (
            <Typography marginTop={2} variant="h3" fontSize={18} lineHeight={2}>
              {pageSubtitle}
            </Typography>
          ) : (
            <Typography marginTop={2} variant="h3" fontSize={18} lineHeight={2}>
              מצטרפים אונליין ומתחילים לחסוך
            </Typography>
          )}
        </Mobile>
      </StyledTextWrapper>

      <ErrorMoadlWrapper>
        <Dialog
          open={error}
          onClose={() => setError(false)}
          aria-labelledby="תפריט תקלות"
          aria-describedby="תפריט תקלות במידה ויש תקלה"
        >
          <Modal
            setError={setError}
            handleClose={() => setError(false)}
            msgHeader="אופס, נראה שיש תקלה"
            msgDiscription={`נשמח אם תנסו שוב במועד מאוחר יותר`}
          />
        </Dialog>
      </ErrorMoadlWrapper>
      <StyledInfo>
        <StyledInfoText>כל השדות עם כוכבית הינם שדות חובה</StyledInfoText>
      </StyledInfo>
      <StyledFormWrapper>
        {fields.map((field) => {
          const hasStar = [
            "firstName",
            "lastName",
            "email",
            "id",
            "phone",
          ].includes(field.id);

          return (
            <TextFieldCustom
              key={field.id}
              id={field.id}
              type={field.type}
              autocomplete={field.autocomplete}
              inputMode={field.inputMode}
              label={`${hasStar ? "*" : ""}${field.label}`}
              control={control}
              helperText={field?.helperText}
            />
          );
        })}
      </StyledFormWrapper>
      <StyleAddressWrapper>
        <Typography
          variant="h1"
          fontSize={25}
          marginBottom={3}
          style={{ textAlign: "right" }}
          color={colors.black}
        >
          כתובת
        </Typography>

        <StyledFormAddressWrapper>
          {/* City Autocomplete */}
          <AutocompleteFieldCustom
            id="city"
            label="*עיר"
            control={control}
            options={cities.map((city) => city["שם_ישוב"])}
          />

          {/* Street Autocomplete */}
          <AutocompleteFieldCustom
            id="street"
            label="*רחוב"
            control={control}
            options={
              selectedCity?.streets.map(
                (street: { [x: string]: any }) => street["שם_רחוב"],
              ) || []
            }
            //  disabled={!selectedCity}
          />

          {/* House / Apartment */}
          <TextFieldCustom
            key="houseNumber"
            id="houseNumber"
            type="text"
            inputMode="numeric"
            autocomplete="address-level3"
            label="מספר בית"
            control={control}
          />
          <TextFieldCustom
            key="apartmentNumber"
            id="apartmentNumber"
            type="text"
            inputMode="numeric"
            autocomplete="address-level4"
            label="מספר דירה"
            control={control}
          />
        </StyledFormAddressWrapper>

        <CheckBoxWrapper>
          <StyledCheckBoxWithText
            name="agreeToTerms"
            control={control}
            required={true}
            error={errors?.agreeToTerms}
            errorText={errors?.agreeToTerms?.message}
          >
            <Typography
              alignSelf={"center"}
              textAlign={"right"}
              color={colors.black}
              variant="body1"
            >
              * מילוי הפרטים מהווה הסכמה ליצור עמך קשר בשיחת טלפון ו/או הודעות
              SMS להשלמת תהליך ההצטרפות. הפרטים יישמרו במאגר המידע של סופר
              פאוור, הרשאית להשתמש בהם בכפוף{" "}
              <Link
                passHref
                href="https://www.supergas-power.co.il/%d7%9e%d7%93%d7%99%d7%a0%d7%99%d7%95%d7%aa-%d7%a4%d7%a8%d7%98%d7%99%d7%95%d7%aa"
                target="_blank"
              >
                למדיניות הפרטיות שלה
              </Link>
              .
              <br />
              * יש למלא את פרטי בעל החוזה בחברת חשמל, הבדיקה נעשית מול נתוני חברת
              חשמל
            </Typography>
            <Typography
              alignSelf={"center"}
              textAlign={"right"}
              color={colors.black}
              variant="body1"
              style={{ marginTop: "8px" }}
            >
              {userVar().referralSpecialTerms}
            </Typography>
          </StyledCheckBoxWithText>
        </CheckBoxWrapper>
      </StyleAddressWrapper>
      {loading && (
        <Typography variant="button" fontSize={20} fontWeight={700}>
          עוד רגע ונשלח לך קוד אימות
        </Typography>
      )}
      <StyledButton
        onClick={handleSubmit(onSubmit, onError)}
        variant="contained"
        color="primary"
        loading={loading ? loading.toString() : undefined}
        disabled={loading}
      >
        {loading ? (
          <CircularProgress size={24} />
        ) : (
          <Typography variant="button" fontSize={20} fontWeight={700}>
            קדימה
          </Typography>
        )}
      </StyledButton>
    </StyledWrapper>
  );
}
